<template>
  <section>
    <SubLinks />
    <span class="text-3xl font-heading font-bold" id="heading-form">
      {{ heading }}
    </span>
    <div class="w-full h-full mt-5 flex flex-col space-y-4 lg:flex-row lg:space-y-0">
      <div class="w-full space-y-4 lg:w-1/2 lg:pr-4 lg:h-[50vh]">
        <TextInput id="show-name-input" label="Show name" v-model="showForm.name" />
        <TextareaInput
          id="show-description-input"
          label="Show description"
          textarea-classes="h-96 lg:h-[55vh]"
          v-model="showForm.description"
        />
      </div>
      <div class="w-full h-full pt-5 space-y-4 lg:w-1/2 lg:pl-4">
        <FileUploadInput
          :initialImageUrl="showForm.image"
          :crop="showForm.crop"
          @updateImageCrop="updateImageCrop"
          accept="image/*"
          id="show-image-input"
          label="Image"
          :file="showImageFile"
          @input="showImageFile = $event"
          :maxWidth="4000"
          :maxHeight="4000"
          helperText="Images must be less than 4000 pixels. For best results, use an image that is at least 608px wide and 405px high."
        />
        <div class="flex justify-end">
          <ButtonDefault
            label="Discard"
            classes="w-40 mr-4"
            @click="discardChanges"
            id="discard-button"
            alt
          />
          <ButtonDefault
            :disabled="isSaving || !isFormValid"
            :label="isSaving ? 'Saving...' : 'Save'"
            @click="submitForm"
            classes="w-40"
            id="save-button"
          />
        </div>
        <ErrorDisplay v-bind="ui.error" class="p-2" />
      </div>
    </div>
  </section>
</template>

<script lang="ts">
import { mapStores } from 'pinia'
import { defineComponent } from 'vue'

import ButtonDefault from '@/common/components/atoms/ButtonDefault.vue'
import ErrorDisplay from '@/common/components/atoms/ErrorDisplay.vue'
import FileUploadInput from '@/common/components/molecules/FileUploadInput.vue'
import SubLinks from '@/common/components/molecules/SubLinks.vue'
import TextInput from '@/common/components/molecules/TextInput.vue'
import TextareaInput from '@/common/components/molecules/TextareaInput.vue'
import { ShowImageCrop, StoreShow, position2crop } from '@/models'
import { useShowStore } from '@/stores/show'
import { blankError, displayHandled } from '@/utils/errors'

import { ShowId } from '@generated/types'

export interface IShowFormDto {
  readonly name: string
  readonly description: string
}

export default defineComponent({
  name: 'ShowAddEdit',
  components: { SubLinks, FileUploadInput, ButtonDefault, TextareaInput, TextInput, ErrorDisplay },
  data() {
    return {
      showForm: {
        name: '',
        description: '',
        image: '',
        crop: position2crop([]),
      } as StoreShow,
      showImageFile: null as File | null,
      hasShowImageChanged: false,
      newImageCrop: null as ShowImageCrop | null,
      isSaving: false,
      ui: {
        error: blankError(),
      },
    }
  },
  methods: {
    updateImageCrop(crop: ShowImageCrop) {
      this.newImageCrop = crop
    },
    async discardChanges() {
      if (this.isAddMode) await this.goToShowList()
      else await this.goToShowDetail(this.showIdToEdit)
    },
    async goToShowDetail(showId: number) {
      await this.$router.push({ name: 'showDetail', params: { id: showId.toString() } })
    },
    async goToShowList() {
      await this.$router.push({ name: 'showList' })
    },
    async submitForm() {
      const name = this.showForm.name?.trim() || ''
      const description = this.showForm.description?.trim() || ''
      if (!this.isFormValid) return

      try {
        this.isSaving = true
        if (this.isAddMode) await this.addNewShow({ name, description })
        else await this.editShow({ name, description })
      } catch (e) {
        this.isSaving = false
        displayHandled(e, this.ui.error)
      }
    },
    async addNewShow({ name, description }: IShowFormDto) {
      const show = await this.showStore.upsertShow({
        name,
        description,
      })
      await this.showStore.updateShowImage({
        id: show.id,
        image: this.showImageFile,
      })
      await this.showStore.updateShowImageCrop({
        id: show.id,
        crop: this.newImageCrop || position2crop([]),
      })
      await this.goToShowDetail(show.id)
    },
    async editShow({ name, description }: IShowFormDto) {
      if (this.hasShowImageChanged) {
        await this.showStore.updateShowImage({
          id: this.showIdToEdit,
          image: this.showImageFile,
        })
      }
      if (this.newImageCrop) {
        await this.showStore.updateShowImageCrop({
          id: this.showIdToEdit,
          crop: this.newImageCrop,
        })
      }
      await this.showStore.upsertShow({ name, description, id: this.showIdToEdit })
      await this.goToShowDetail(this.showIdToEdit)
    },
    async fetchShowToEdit() {
      if (this.isAddMode) return
      try {
        const showToEdit = await this.showStore.fetchShow(this.showIdToEdit)
        this.hasShowImageChanged = false
        this.showForm = { ...showToEdit }
      } catch (error) {
        displayHandled(error, this.ui.error)
      }
    },
  },
  async mounted() {
    if (this.isAddMode) this.hasShowImageChanged = true
    else await this.fetchShowToEdit()
  },
  watch: {
    showImageFile() {
      this.hasShowImageChanged = true
    },
    async '$route.params.id'() {
      await this.fetchShowToEdit()
    },
  },
  computed: {
    ...mapStores(useShowStore),
    isFormValid(): boolean {
      if (!this.showForm.name?.trim()) return false
      if (this.hasShowImageChanged) return this.showImageFile !== null
      return true
    },
    showIdToEdit(): ShowId {
      const id: string = this.$route.params.id as string
      return isNaN(+id) ? 0 : parseInt(id)
    },
    isAddMode(): boolean {
      return this.showIdToEdit <= 0
    },
    heading(): string {
      return this.isAddMode ? 'Adding a Show' : 'Editing a Show'
    },
  },
})
</script>
